import { DiagnosticsFooter, EmptyState, usePayload } from './primitives';
import styles from './styles.module.css';
import s from './PricingViewer.module.css';

const BAND_CLASS = {
  'Entry':         s.bandEntry,
  'Affordable':    s.bandAffordable,
  'Middle Market': s.bandMiddle,
  'Premium':       s.bandPremium,
  'Luxury':        s.bandLuxury,
};

const RETURN_CLASS = {
  'Strong':   s.returnStrong,
  'Standard': s.returnStandard,
  'Weak':     s.returnWeak,
  'Unknown':  s.returnUnknown,
};

const PricingViewer = ({ report }) => {
  const payload = usePayload(report);
  if (!report) return <EmptyState />;

  const fields = Array.isArray(payload?.fields) ? payload.fields : [];
  const byKey = Object.fromEntries(fields.map((f) => [f.key, f]));

  const lowestPrice     = byKey['lowest_product_price']?.value   || null;
  const medianPrice     = byKey['median_product_price']?.value   || null;
  const highestPrice    = byKey['highest_product_price']?.value  || null;
  const rangeWidth      = byKey['price_range_width']?.value      || null;
  const coreBand        = byKey['core_price_band']?.value        || null;
  const priceBandLabel  = byKey['price_band_label']?.value       || null;
  const discountPresence  = byKey['discount_presence']?.value    || null;
  const discountIntensity = byKey['discount_intensity']?.value   || null;
  const avgMarkdown       = byKey['average_markdown_pct']?.value || null;
  const promoPattern      = byKey['promotion_pattern']?.value    || null;
  const bundlePresence    = byKey['bundle_presence']?.value      || null;
  const freeShipping      = byKey['free_shipping_signal']?.value || null;
  const returnStrength    = byKey['return_policy_strength']?.value || null;
  const returnEvidence    = Array.isArray(byKey['return_policy_strength']?.evidence)
    ? byKey['return_policy_strength'].evidence : [];

  const summary    = payload?.summary || null;
  const confidence = payload?.diagnostics?.profile_confidence_level || null;
  const missing    = Array.isArray(payload?.diagnostics?.missing_data_flags)
    ? payload.diagnostics.missing_data_flags : [];

  return (
    <div className={styles.viewer}>

      {/* Summary */}
      {summary && (
        <div className={s.summaryBlock}>
          <div className={s.summaryHeader}>
            <span className={s.summaryTitle}>Summary</span>
            {confidence && (
              <span className={`${s.confidenceBadge} ${confidence === 'High' ? s.confHigh : confidence === 'Medium' ? s.confMedium : s.confLow}`}>
                {confidence}
              </span>
            )}
          </div>
          <p className={s.summaryText}>{summary}</p>
        </div>
      )}

      {/* Price position */}
      <div className={s.priceBlock}>
        <div className={s.blockHeader}>
          <span className={s.blockTitle}>Price Position</span>
          {coreBand && <span className={s.coreBandBadge}>{coreBand} core</span>}
        </div>

        {/* Band label */}
        {priceBandLabel && (
          <div className={`${s.bandRow} ${BAND_CLASS[priceBandLabel] || ''}`}>
            <span className={s.bandLabel}>Price Band</span>
            <span className={s.bandValue}>{priceBandLabel}</span>
          </div>
        )}

        {/* Low / Median / High */}
        {(lowestPrice || medianPrice || highestPrice) && (
          <div className={s.priceStatsRow}>
            {lowestPrice && (
              <div className={s.priceStat}>
                <span className={s.priceStatVal}>{lowestPrice}</span>
                <span className={s.priceStatLbl}>Low</span>
              </div>
            )}
            {lowestPrice && medianPrice && <span className={s.priceStatDivider} />}
            {medianPrice && (
              <div className={`${s.priceStat} ${s.priceStatMedian}`}>
                <span className={s.priceStatVal}>{medianPrice}</span>
                <span className={s.priceStatLbl}>Median</span>
              </div>
            )}
            {medianPrice && highestPrice && <span className={s.priceStatDivider} />}
            {highestPrice && (
              <div className={s.priceStat}>
                <span className={s.priceStatVal}>{highestPrice}</span>
                <span className={s.priceStatLbl}>High</span>
              </div>
            )}
          </div>
        )}

        {rangeWidth && (
          <div className={s.rangeRow}>
            <span className={s.rangeLabel}>Range width</span>
            <span className={s.rangeValue}>{rangeWidth}</span>
          </div>
        )}
      </div>

      {/* Discount & promotions */}
      <div className={s.discountBlock}>
        <div className={s.blockHeader}>
          <span className={s.blockTitle}>Discount & Promotions</span>
        </div>
        <div className={s.discountGrid}>
          {discountPresence && (
            <div className={s.discountAttr}>
              <span className={s.discountAttrLabel}>Presence</span>
              <span className={`${s.discountBadge} ${discountPresence === 'Yes' ? s.discountYes : s.discountNo}`}>
                {discountPresence}
              </span>
            </div>
          )}
          {discountIntensity && (
            <div className={s.discountAttr}>
              <span className={s.discountAttrLabel}>Intensity</span>
              <span className={s.discountAttrValue}>{discountIntensity}</span>
            </div>
          )}
          {avgMarkdown && (
            <div className={s.discountAttr}>
              <span className={s.discountAttrLabel}>Avg Markdown</span>
              <span className={s.discountAttrValue}>{avgMarkdown}</span>
            </div>
          )}
          {promoPattern && (
            <div className={s.discountAttr}>
              <span className={s.discountAttrLabel}>Pattern</span>
              <span className={s.discountAttrValue}>{promoPattern}</span>
            </div>
          )}
          {bundlePresence && (
            <div className={s.discountAttr}>
              <span className={s.discountAttrLabel}>Bundles</span>
              <span className={`${s.discountBadge} ${bundlePresence === 'Yes' ? s.bundleYes : s.discountNo}`}>
                {bundlePresence}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Commercial signals */}
      <div className={s.signalsRow}>
        {freeShipping && (
          <div className={s.signalCard}>
            <span className={s.signalLabel}>Free Shipping</span>
            <span className={`${s.signalValue} ${freeShipping.startsWith('Yes') ? s.signalGreen : s.signalNeutral}`}>
              {freeShipping}
            </span>
          </div>
        )}
        {returnStrength && (
          <div className={s.signalCard}>
            <span className={s.signalLabel}>Return Policy</span>
            <span className={`${s.signalValue} ${RETURN_CLASS[returnStrength] || ''}`}>
              {returnStrength}
            </span>
            {returnEvidence.length > 0 && (
              <ul className={s.signalEvidence}>
                {returnEvidence.slice(0, 2).map((e, i) => (
                  <li key={i} className={s.signalEvidenceItem}>{e}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <DiagnosticsFooter missingFlags={missing} />
    </div>
  );
};

export default PricingViewer;
